import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, AlertCircle, Loader2 } from 'lucide-react';
import { useFactory } from '../../../context/FactoryContext';
import { useCurrentEmployee } from '../../../hooks/useCurrentEmployee';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  isLaborRestrictedViewer,
  isProdSupervisorTeamScoped,
  isLaborAutoPatternScoped,
} from '../../../utils/roleChecks';
import { filterTeamsByRole, buildTeamEmployeeFilter } from '../../../utils/teamScope';
import { teamService } from '../empleados/services/teamService';
import { employeeService } from '../empleados/services/employeeService';
import { laborScheduleService } from '../calendario/services/laborScheduleService';
import {
  resolveLaborFactoryId,
  getEmployeeDisplayName,
} from '../calendario/utils/laborFactoryUtils';
import { LaborPatternCoverageBanner } from './components/LaborPatternCoverageBanner';
import { LaborAttendanceTable } from './components/LaborAttendanceTable';
import { validateAttendanceDateRange } from './utils/attendanceDateUtils';

const todayIso = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
const NO_PATTERN_FILTER = 'none';

const Asistencia = () => {
  const { selectedFactory, factories } = useFactory();
  const currentYear = new Date().getFullYear();
  const { roleKeys, isLoading: permissionsLoading } = usePermissions();
  const isViewer = useMemo(() => isLaborRestrictedViewer(roleKeys), [roleKeys]);
  const isSupervisorScoped = useMemo(() => isProdSupervisorTeamScoped(roleKeys), [roleKeys]);
  const hasAutoPattern = useMemo(() => isLaborAutoPatternScoped(roleKeys), [roleKeys]);

  const [attendancePatternId, setAttendancePatternId] = useState('');
  const [attendanceTeamId, setAttendanceTeamId] = useState('');
  const [attendanceEmployeeId, setAttendanceEmployeeId] = useState('');
  const [attendanceDateFrom, setAttendanceDateFrom] = useState(todayIso());
  const [attendanceDateTo, setAttendanceDateTo] = useState(todayIso());
  const attendanceRefetchRef = useRef(null);
  const [isAttendanceFetching, setIsAttendanceFetching] = useState(false);

  const { employeeId: currentEmployeeId, employeeName: currentEmployeeName, isReady: hasCurrentEmployee } =
    useCurrentEmployee();

  const factoryId = useMemo(
    () => resolveLaborFactoryId(selectedFactory, factories),
    [selectedFactory, factories]
  );
  const factoryBase = selectedFactory || '';

  const dateRangeValidation = useMemo(
    () => validateAttendanceDateRange(attendanceDateFrom, attendanceDateTo),
    [attendanceDateFrom, attendanceDateTo]
  );

  const isSingleDay = attendanceDateFrom === attendanceDateTo;

  const teamsQueryReady =
    Boolean(factoryId) && (!isSupervisorScoped || hasCurrentEmployee);

  const { data: calendarsForYear = [] } = useQuery({
    queryKey: ['labor-calendars', factoryId, currentYear],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId, currentYear),
    enabled: Boolean(factoryId),
  });

  const { data: calendarsAll = [] } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: Boolean(factoryId) && calendarsForYear.length === 0,
  });

  const activeCalendarId = useMemo(() => {
    if (calendarsForYear.length > 0) return calendarsForYear[0].id;
    if (calendarsAll.length > 0) return calendarsAll[0].id;
    return null;
  }, [calendarsForYear, calendarsAll]);

  const { data: attendancePatternsForSelect = [], isLoading: loadingAttendancePatterns } = useQuery({
    queryKey: ['labor-patterns-by-calendar', activeCalendarId],
    queryFn: () => laborScheduleService.listPatternsByCalendar(activeCalendarId),
    enabled: Boolean(activeCalendarId) && !hasAutoPattern,
    staleTime: 120_000,
    select: (data) =>
      [...data].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  });

  const { data: attendanceTeams = [], isLoading: loadingAttendanceTeams } = useQuery({
    queryKey: ['labor-teams-attendance', factoryId, currentEmployeeId, isSupervisorScoped],
    queryFn: () => teamService.getTeams(),
    enabled: teamsQueryReady,
    staleTime: 60_000,
    select: (teams) =>
      filterTeamsByRole(teams, {
        factoryId,
        roleKeys,
        leaderEmployeeId: currentEmployeeId,
      }).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  });

  const { data: attendanceTeamMembers = [], isLoading: loadingAttendanceTeamMembers } = useQuery({
    queryKey: ['labor-team-members-attendance', attendanceTeamId],
    queryFn: () => teamService.getTeamMembers(attendanceTeamId),
    enabled: Boolean(attendanceTeamId),
  });

  const { data: supervisorTeamPattern, isLoading: loadingSupervisorPattern } = useQuery({
    queryKey: ['team-active-pattern', attendanceTeamId, attendanceDateFrom],
    queryFn: () => teamService.getTeamActivePattern(attendanceTeamId, attendanceDateFrom),
    enabled: isSupervisorScoped && Boolean(attendanceTeamId) && Boolean(attendanceDateFrom),
    staleTime: 60_000,
  });

  const { data: employeeActivePattern, isLoading: loadingEmployeePattern } = useQuery({
    queryKey: ['employee-active-pattern', currentEmployeeId, attendanceDateFrom],
    queryFn: () => laborScheduleService.getEmployeeActivePattern(currentEmployeeId, attendanceDateFrom),
    enabled: isViewer && hasCurrentEmployee && Boolean(attendanceDateFrom),
    staleTime: 60_000,
  });

  const autoPattern = isSupervisorScoped ? supervisorTeamPattern : employeeActivePattern;
  const loadingAutoPattern = isSupervisorScoped ? loadingSupervisorPattern : loadingEmployeePattern;

  const attendanceTeamFilter = useMemo(() => {
    if (!attendanceTeamId) return null;
    const team = attendanceTeams.find((t) => String(t.id) === String(attendanceTeamId));
    return buildTeamEmployeeFilter(team, attendanceTeamMembers, {
      loading: loadingAttendanceTeamMembers,
    });
  }, [
    attendanceTeamId,
    attendanceTeams,
    attendanceTeamMembers,
    loadingAttendanceTeamMembers,
  ]);

  const { data: factoryEmployees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-attendance-employees', factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true, null),
    enabled: Boolean(factoryBase) && !isViewer,
    staleTime: 60_000,
  });

  const selectableEmployees = useMemo(() => {
    const sorted = [...factoryEmployees].sort((a, b) =>
      getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b), 'es')
    );
    if (isSupervisorScoped) {
      if (!attendanceTeamFilter?.employeeIds?.size) return [];
      return sorted.filter((emp) => attendanceTeamFilter.employeeIds.has(Number(emp.id)));
    }
    if (attendanceTeamId && attendanceTeamFilter?.employeeIds) {
      return sorted.filter((emp) => attendanceTeamFilter.employeeIds.has(Number(emp.id)));
    }
    return sorted;
  }, [factoryEmployees, isSupervisorScoped, attendanceTeamFilter, attendanceTeamId]);

  useEffect(() => {
    setAttendanceTeamId('');
    setAttendanceEmployeeId('');
    setAttendancePatternId('');
  }, [factoryId]);

  useEffect(() => {
    if (isSupervisorScoped && attendanceTeams.length > 0) {
      setAttendanceTeamId(String(attendanceTeams[0].id));
    }
  }, [isSupervisorScoped, attendanceTeams, factoryId]);

  useEffect(() => {
    if (!hasAutoPattern) return;
    if (autoPattern?.pattern_id) {
      setAttendancePatternId(String(autoPattern.pattern_id));
    } else {
      setAttendancePatternId('');
    }
  }, [hasAutoPattern, autoPattern]);

  useEffect(() => {
    if (!attendanceEmployeeId) return;
    if (attendanceTeamFilter?.employeeIds && !attendanceTeamFilter.employeeIds.has(Number(attendanceEmployeeId))) {
      setAttendanceEmployeeId('');
    }
  }, [attendanceTeamId, attendanceTeamFilter, attendanceEmployeeId]);

  const viewerSelfFilter = useMemo(() => {
    if (!isViewer || !currentEmployeeId) return null;
    return {
      teamId: 'viewer-self',
      name: currentEmployeeName || `Empleado #${currentEmployeeId}`,
      employeeIds: new Set([Number(currentEmployeeId)]),
      loading: false,
    };
  }, [isViewer, currentEmployeeId, currentEmployeeName]);

  const effectiveEmployeeFilter = useMemo(() => {
    if (isViewer) return viewerSelfFilter;
    if (attendanceEmployeeId) {
      const emp = selectableEmployees.find((e) => String(e.id) === String(attendanceEmployeeId));
      return {
        teamId: 'employee',
        name: emp ? getEmployeeDisplayName(emp) : `Empleado #${attendanceEmployeeId}`,
        employeeIds: new Set([Number(attendanceEmployeeId)]),
        loading: false,
      };
    }
    return attendanceTeamFilter;
  }, [
    isViewer,
    viewerSelfFilter,
    attendanceEmployeeId,
    selectableEmployees,
    attendanceTeamFilter,
  ]);

  const autoPatternLabel = useMemo(() => {
    if (!hasAutoPattern) return '';
    if (loadingAutoPattern) {
      return isSupervisorScoped ? 'Cargando patrón del equipo…' : 'Cargando tu patrón…';
    }
    if (autoPattern?.pattern_name) return autoPattern.pattern_name;
    return 'Sin patrón asignado';
  }, [hasAutoPattern, loadingAutoPattern, isSupervisorScoped, autoPattern]);

  const rosterLoading =
    (!isViewer && loadingEmployees) ||
    (Boolean(attendanceTeamId) && loadingAttendanceTeamMembers) ||
    (isSupervisorScoped && loadingAttendanceTeams);

  const rosterReady =
    dateRangeValidation.valid &&
    (!isSupervisorScoped || Boolean(attendanceTeamId)) &&
    (!isViewer || hasCurrentEmployee) &&
    !rosterLoading;

  const rosterEmployeeIds = useMemo(() => {
    if (isViewer) {
      return currentEmployeeId ? [Number(currentEmployeeId)] : [];
    }
    if (attendanceEmployeeId) {
      return [Number(attendanceEmployeeId)].filter((id) => id > 0);
    }
    return selectableEmployees.map((emp) => Number(emp.id)).filter((id) => id > 0);
  }, [isViewer, currentEmployeeId, attendanceEmployeeId, selectableEmployees]);

  const canLoadAttendance = rosterReady && rosterEmployeeIds.length > 0;

  const attendancePendingMessage = rosterLoading
    ? (loadingAttendanceTeamMembers
      ? 'Cargando miembros del equipo…'
      : loadingAttendanceTeams && isSupervisorScoped
        ? 'Cargando equipos…'
        : 'Cargando empleados…')
    : '';

  const handleRefresh = () => {
    if (canLoadAttendance) {
      attendanceRefetchRef.current?.();
    }
  };

  if (permissionsLoading) return null;

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <ClipboardList className="w-8 h-8 text-blue-400" />
          Asistencia
        </h1>
        <p className="text-gray-400">
          {isSupervisorScoped
            ? 'Validación de fichajes de tu equipo por rango de fechas.'
            : isViewer
              ? 'Consulta tu asistencia por rango de fechas.'
              : 'Validación de fichajes por patrón y rango de fechas. La fábrica del header filtra equipos y calendario laboral.'}
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-end bg-gray-800/80 border border-gray-700 rounded-lg p-4">
        {hasAutoPattern ? (
          <div className="flex-1 min-w-[200px] max-w-sm">
            <span className="block text-xs text-gray-400 mb-1">
              {isSupervisorScoped ? 'Patrón del equipo' : 'Tu patrón'}
            </span>
            <p
              className={`rounded-lg border px-3 py-2 text-sm ${
                autoPattern?.pattern_id
                  ? 'bg-gray-700/60 border-gray-600 text-white'
                  : 'bg-amber-900/20 border-amber-700/50 text-amber-300'
              }`}
            >
              {autoPatternLabel}
            </p>
            {autoPattern?.message && (
              <p className="mt-1 text-xs text-amber-400/90">{autoPattern.message}</p>
            )}
          </div>
        ) : (
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label htmlFor="attendance-pattern" className="block text-xs text-gray-400 mb-1">
              Patrón
            </label>
            <select
              id="attendance-pattern"
              value={attendancePatternId}
              onChange={(e) => setAttendancePatternId(e.target.value)}
              disabled={!activeCalendarId || loadingAttendancePatterns}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="">
                {loadingAttendancePatterns ? 'Cargando…' : 'Todos'}
              </option>
              <option value={NO_PATTERN_FILTER}>Sin patrón</option>
              {attendancePatternsForSelect.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="attendance-date-from" className="block text-xs text-gray-400 mb-1">
            Desde
          </label>
          <input
            id="attendance-date-from"
            type="date"
            value={attendanceDateFrom}
            onChange={(e) => setAttendanceDateFrom(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label htmlFor="attendance-date-to" className="block text-xs text-gray-400 mb-1">
            Hasta
          </label>
          <input
            id="attendance-date-to"
            type="date"
            value={attendanceDateTo}
            onChange={(e) => setAttendanceDateTo(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {!isViewer && (
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label htmlFor="attendance-team" className="block text-xs text-gray-400 mb-1">
              {isSupervisorScoped ? 'Tu equipo' : 'Equipo (opcional)'}
            </label>
            <select
              id="attendance-team"
              value={attendanceTeamId}
              onChange={(e) => {
                setAttendanceTeamId(e.target.value);
                setAttendanceEmployeeId('');
              }}
              disabled={
                !factoryId ||
                loadingAttendanceTeams ||
                (isSupervisorScoped && attendanceTeams.length <= 1)
              }
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-70"
            >
              {isSupervisorScoped ? (
                attendanceTeams.length === 0 ? (
                  <option value="">Sin equipo asignado</option>
                ) : (
                  attendanceTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))
                )
              ) : (
                <>
                  <option value="">
                    {loadingAttendanceTeams ? 'Cargando…' : 'Todos los equipos'}
                  </option>
                  {attendanceTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        )}

        <div className="flex-1 min-w-[200px] max-w-sm">
          <label htmlFor="attendance-employee" className="block text-xs text-gray-400 mb-1">
            {isSupervisorScoped ? 'Compañero de equipo' : 'Empleado'}
          </label>
          {isViewer ? (
            <div className="w-full bg-gray-700/50 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm">
              {currentEmployeeName || '—'}
            </div>
          ) : (
            <select
              id="attendance-employee"
              value={attendanceEmployeeId}
              onChange={(e) => setAttendanceEmployeeId(e.target.value)}
              disabled={
                !factoryId ||
                loadingEmployees ||
                (isSupervisorScoped && !attendanceTeamId) ||
                (Boolean(attendanceTeamId) && loadingAttendanceTeamMembers)
              }
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-70"
            >
              <option value="">
                {attendanceTeamId
                  ? loadingAttendanceTeamMembers
                    ? 'Cargando miembros…'
                    : 'Todos del equipo'
                  : loadingEmployees
                    ? 'Cargando…'
                    : 'Todos los empleados'}
              </option>
              {selectableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {getEmployeeDisplayName(emp)}
                </option>
              ))}
            </select>
          )}
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={!canLoadAttendance || isAttendanceFetching}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg border border-gray-600 disabled:opacity-50"
        >
          {isAttendanceFetching && <Loader2 className="w-4 h-4 animate-spin" />}
          Actualizar
        </button>
      </div>

      {!dateRangeValidation.valid && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{dateRangeValidation.error}</span>
        </div>
      )}

      {!factoryId && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Selecciona una fábrica en el header para ver la asistencia.</span>
        </div>
      )}

      {factoryId && isViewer && !hasCurrentEmployee && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Tu usuario no está vinculado a un empleado; no puedes consultar tu asistencia.</span>
        </div>
      )}

      {factoryId && !isViewer && !hasCurrentEmployee && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Tu usuario no está vinculado a un empleado; no podrás validar fichajes.</span>
        </div>
      )}

      {factoryId && isSupervisorScoped && !loadingAttendanceTeams && attendanceTeams.length === 0 && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>No tienes ningún equipo asignado como líder.</span>
        </div>
      )}

      {factoryId &&
        hasAutoPattern &&
        !loadingAutoPattern &&
        !autoPattern?.pattern_id &&
        (isSupervisorScoped ? Boolean(attendanceTeamId) : hasCurrentEmployee) && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>
            {autoPattern?.message ||
              (isSupervisorScoped
                ? 'El equipo no tiene patrón asignado para la fecha de inicio del rango.'
                : 'No tienes patrón asignado para la fecha de inicio del rango.')}
          </span>
        </div>
      )}

      {factoryId && (
        <>
          {isSingleDay && attendancePatternId !== NO_PATTERN_FILTER && (
            <LaborPatternCoverageBanner
              patternId={attendancePatternId}
              date={attendanceDateFrom}
              fetchEnabled={dateRangeValidation.valid && Boolean(attendancePatternId)}
            />
          )}
          <LaborAttendanceTable
            rosterEmployeeIds={rosterEmployeeIds}
            patternFilterId={hasAutoPattern ? '' : attendancePatternId}
            dateFrom={attendanceDateFrom}
            dateTo={attendanceDateTo}
            refetchRef={attendanceRefetchRef}
            teamFilter={effectiveEmployeeFilter}
            fetchEnabled={canLoadAttendance}
            pendingMessage={attendancePendingMessage}
            rosterEmpty={rosterReady && rosterEmployeeIds.length === 0}
            rosterEmptyMessage={
              isSupervisorScoped || attendanceTeamId
                ? 'El equipo no tiene miembros asignados.'
                : 'No hay empleados en esta fábrica.'
            }
            validatorEmployeeId={currentEmployeeId}
            readOnly={isViewer}
            onFetchingChange={setIsAttendanceFetching}
          />
        </>
      )}
    </div>
  );
};

export default Asistencia;
